import { isValidCountryCode } from './countryCodes';

export interface DeviceModelConfig {
  /** Model prefix/identifier (e.g., "SEI", "SEO") */
  prefix: string;

  /** Display name for the prefix (e.g., "SmartEco Indoor", "SmartEco Outdoor") */
  displayName: string;

  /** Length of version number in serial (e.g., 3 for "100") */
  versionLength: number;

  /** Length of model identifier in serial (e.g., 1 for "C", "M", "A") */
  modelLength: number;

  /** Position where model letter appears (after prefix and version) */
  modelPosition: number;

  /** Allowed model letters for this prefix (e.g., ["C", "M", "A"]) */
  validModelLetters: string[];

  /** Description for documentation */
  description: string;
}

export const DEVICE_MODELS: DeviceModelConfig[] = [
  {
    prefix: 'SEI',
    displayName: 'SmartEco Indoor',
    versionLength: 3,      // "100" - Device version indicator
    modelLength: 1,        // Single letter: C, M, A (or future: P, E, S, B, L, etc.)
    modelPosition: 6,      // Position after "SEI100"
    validModelLetters: ['C', 'M', 'A'],
    description: 'SmartEco Indoor Air Quality Monitor',
  },

];

/** Year must be current year or at most 5 years in the past (2-digit). */
const MIN_YEAR_OFFSET = 5;

function getValidYearRange(): { min: number; max: number } {
  const currentYearLastTwo = new Date().getFullYear() % 100;
  return {
    min: currentYearLastTwo - MIN_YEAR_OFFSET,
    max: currentYearLastTwo,
  };
}

function isValidYear(year: string): boolean {
  if (!/^\d{2}$/.test(year)) return false;
  const yearNum = parseInt(year, 10);
  const { min, max } = getValidYearRange();
  return yearNum >= min && yearNum <= max;
}

/**
 * Get Model Number configuration by prefix
 * @param prefix - Model prefix (e.g., "SEI", "SEO")
 * @returns Model Number configuration or undefined if not found
 */
export function getDeviceModelByPrefix(prefix: string): DeviceModelConfig | undefined {
  const normalizedPrefix = prefix.toUpperCase().trim();
  return DEVICE_MODELS.find(model => model.prefix === normalizedPrefix);
}

/**
 * Extract model letter from serial number
 * @param serialNumber - Full serial number (e.g., "SEI100C784250001")
 * @returns Model letter (e.g., "C", "M", "A") or null if invalid
 * 
 * Supported formats:
 * - SEI100C784250001 → Returns "C" (Core)
 * - SEI100M784250002 → Returns "M" (Mini)
 * - SEI100A784250003 → Returns "A" (Advance)
 */
export function extractModelLetterFromSerial(serialNumber: string): string | null {
  if (!serialNumber || serialNumber.length < 7) return null;
  
  const cleanSerial = serialNumber.replace(/\s+/g, '').toUpperCase();
  
  // Parse SEI format (SEI100C...)
  for (const model of DEVICE_MODELS) {
    if (cleanSerial.startsWith(model.prefix)) {
      const modelLetter = cleanSerial.charAt(model.modelPosition);
      return modelLetter || null;
    }
  }
  
  return null;
}
 
/**
 * Build a strict regex that a serial number must match for a given model config.
 * Format: PREFIX + version digits + allowed model letters + country(3) + year(2) + deviceId(4)
 * Example for SEI: ^SEI\d{3}[CMA]\d{3}\d{2}\d{4}$  →  SEI100C784250001
 *
 * The model letter class is built from `validModelLetters` so adding a new model
 * in DEVICE_MODELS automatically extends validation.
 */
export function getSerialPatternForModel(model: DeviceModelConfig): RegExp {
  const letterClass = model.validModelLetters.join('');
  return new RegExp(
    `^${model.prefix}\\d{${model.versionLength}}[${letterClass}]{${model.modelLength}}\\d{3}\\d{2}\\d{4}$`
  );
}

export function parseSerialNumber(serialNumber: string): {
  prefix: string;
  version: string;
  modelLetter: string;
  countryCode: string;
  year: string;
  deviceId: string;
} | null {
  if (!serialNumber) return null;

  const cleanSerial = serialNumber.replace(/\s+/g, '').toUpperCase();

  for (const model of DEVICE_MODELS) {
    if (!getSerialPatternForModel(model).test(cleanSerial)) continue;

    const prefix = model.prefix;
    const startPos = prefix.length;
    const version = cleanSerial.substring(startPos, startPos + model.versionLength);
    const modelLetter = cleanSerial.substring(startPos + model.versionLength, startPos + model.versionLength + model.modelLength);

    const afterModel = startPos + model.versionLength + model.modelLength;
    const countryCode = cleanSerial.substring(afterModel, afterModel + 3);
    const year = cleanSerial.substring(afterModel + 3, afterModel + 5);
    const deviceId = cleanSerial.substring(afterModel + 5, afterModel + 9);

    return { prefix, version, modelLetter, countryCode, year, deviceId };
  }

  return null;
}

 
export function matchDeviceTypeByModelLetter(
  modelLetter: string,
  deviceTypes: { id: number; typeName: string; description: string }[]
): { id: number; typeName: string; description: string } | null {
  if (!modelLetter || !deviceTypes || deviceTypes.length === 0) return null;
  
  const normalizedModelLetter = modelLetter.toUpperCase().trim();
  
  // Match modelLetter with typeName in API response
  const matched = deviceTypes.find(type => 
    type.typeName.toUpperCase() === normalizedModelLetter
  );
  
  return matched || null;
}

/**
 * Extract Model Number Number from Serial Number
 * Model Number = SEI + Version + Model Letter
 * 
 * @param serialNumber - Full serial number (e.g., "SEI100C784250001")
 * @returns Model Number number (e.g., "SEI100C") or null
 * 
 * Format: SEI[Version][Model]
 * Examples:
 * - SEI100C784250001 → "SEI100C" (SmartEco Indoor, Version 100, CORE)
 * - SEI100M784250002 → "SEI100M" (SmartEco Indoor, Version 100, MINI)
 * - SEI100A784250003 → "SEI100A" (SmartEco Indoor, Version 100, Advance)
 */
export function extractDeviceModelNumber(serialNumber: string): string | null {
  if (!serialNumber) return null;
  
  const cleanSerial = serialNumber.replace(/\s+/g, '').toUpperCase();
  
  // Parse SEI format
  const parsed = parseSerialNumber(cleanSerial);
  
  if (parsed) {
    // Format: SEI + Version + ModelLetter
    // Example: SEI + 100 + C = SEI100C
    return `${parsed.prefix}${parsed.version}${parsed.modelLetter}`;
  }
  
  // For legacy/unknown formats, return as-is or null
  return null;
}

/**
 * Check if a serial number is valid SEI format (full strict check: prefix, model letter,
 * country code, year, and device ID).
 * @param serialNumber - Serial number to check
 * @returns True if valid SEI format
 */
export function isValidSEIFormat(serialNumber: string): boolean {
  return validateSerialNumber(serialNumber).valid;
}

export interface SerialValidationResult {
  valid: boolean;
  /** User-facing error message when `valid` is false. */
  error?: string;
}

/**
 * Strict, granular validation of a SmartEco serial number.
 * Checks, in order: prefix → overall shape → model letter → country code → year.
 * Returns a specific error message pinpointing which part failed.
 */
export function validateSerialNumber(serialNumber: string): SerialValidationResult {
  if (!serialNumber || !serialNumber.trim()) {
    return { valid: false, error: 'Please enter a serial number.' };
  }

  const cleanSerial = serialNumber.replace(/\s+/g, '').toUpperCase();

  const matchedModel = DEVICE_MODELS.find(m => cleanSerial.startsWith(m.prefix));
  if (!matchedModel) {
    const prefixes = DEVICE_MODELS.map(m => m.prefix).join(', ');
    return {
      valid: false,
      error: `Serial number must start with ${prefixes} (e.g. SEI100C784250001).`,
    };
  }

  const expectedLength =
    matchedModel.prefix.length +
    matchedModel.versionLength +
    matchedModel.modelLength +
    3 /* country */ +
    2 /* year */ +
    4 /* device id */;
  if (cleanSerial.length !== expectedLength) {
    return {
      valid: false,
      error: `Serial number must be ${expectedLength} characters (e.g. SEI100C784250001).`,
    };
  }

  // Shape check: prefix + digits + letter(s) + country digits + year digits + deviceId digits
  const shapePattern = new RegExp(
    `^${matchedModel.prefix}\\d{${matchedModel.versionLength}}[A-Z]{${matchedModel.modelLength}}\\d{3}\\d{2}\\d{4}$`
  );
  if (!shapePattern.test(cleanSerial)) {
    return {
      valid: false,
      error: 'Serial number format is invalid. Expected format: SEI100C784250001.',
    };
  }

  const startPos = matchedModel.prefix.length;
  const modelLetter = cleanSerial.substring(
    startPos + matchedModel.versionLength,
    startPos + matchedModel.versionLength + matchedModel.modelLength
  );
  if (!matchedModel.validModelLetters.includes(modelLetter)) {
    return {
      valid: false,
      error: `Unknown model "${modelLetter}". Valid models are ${matchedModel.validModelLetters.join(', ')} (Core, Mini, Advance).`,
    };
  }

  const afterModel = startPos + matchedModel.versionLength + matchedModel.modelLength;
  const countryCode = cleanSerial.substring(afterModel, afterModel + 3);
  if (!isValidCountryCode(countryCode)) {
    return {
      valid: false,
      error: `"${countryCode}" is not a recognized country code. Check the serial number printed on your device.`,
    };
  }

  const year = cleanSerial.substring(afterModel + 3, afterModel + 5);
  if (!isValidYear(year)) {
    const { min, max } = getValidYearRange();
    const pad = (n: number) => n.toString().padStart(2, '0');
    return {
      valid: false,
      error: `Manufacturing year "${year}" is out of range. Expected ${pad(min)}–${pad(max)}.`,
    };
  }

  return { valid: true };
}

/**
 * Get Model Number configuration by prefix
 * @param prefix - Model prefix (e.g., "SEI")
 * @returns Model Number configuration or undefined if not found
 */
export function getDeviceConfigByPrefix(prefix: string): DeviceModelConfig | undefined {
  const normalizedPrefix = prefix.toUpperCase().trim();
  return DEVICE_MODELS.find(model => model.prefix === normalizedPrefix);
}

/**
 * Check if a model prefix matches any Model Number
 * @param prefix - Model prefix to check
 * @returns True if prefix matches a known Model Number
 */
export function isValidModelPrefix(prefix: string): boolean {
  return getDeviceModelByPrefix(prefix) !== undefined;
}

/**
 * Get all model prefixes
 * @returns Array of all model prefixes
 */
export function getAllModelPrefixes(): string[] {
  return DEVICE_MODELS.map(model => model.prefix);
}

